import { type FormEvent, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { advisoryIdPattern, canonicalAdvisoryId, ECOSYSTEMS, ECOSYSTEM_LABELS } from '@repo/shared';
import type { Ecosystem } from '@repo/shared';
import styles from './pages.module.css';
import homeStyles from './HomePage.module.css';

const ECOSYSTEM_PLACEHOLDERS: Record<Ecosystem, string> = {
  NPM: 'e.g. lodash or @scope/package',
  PYPI: 'e.g. requests',
  GO: 'e.g. github.com/gin-gonic/gin',
  MAVEN: 'e.g. com.google.guava:guava',
  CARGO: 'e.g. serde',
  NUGET: 'e.g. Newtonsoft.Json',
  RUBYGEMS: 'e.g. rails',
};

type SearchMode = 'package' | 'advisory';

/** Landing page: explicit package vs advisory search. */
export function HomePage() {
  const [mode, setMode] = useState<SearchMode>('package');
  const [ecosystem, setEcosystem] = useState<Ecosystem>('NPM');
  const [query, setQuery] = useState('');
  const [advisoryError, setAdvisoryError] = useState(false);
  const navigate = useNavigate();

  function switchMode(next: SearchMode) {
    setMode(next);
    setQuery('');
    setAdvisoryError(false);
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const trimmed = query.trim();
    if (!trimmed) return;

    if (mode === 'advisory') {
      if (!advisoryIdPattern.test(trimmed)) {
        setAdvisoryError(true);
        return;
      }
      void navigate({ to: '/advisory/$id', params: { id: canonicalAdvisoryId(trimmed) } });
      return;
    }

    // Safety net: a pasted CVE/GHSA id in package mode still goes to the advisory page.
    if (advisoryIdPattern.test(trimmed)) {
      void navigate({ to: '/advisory/$id', params: { id: canonicalAdvisoryId(trimmed) } });
      return;
    }
    void navigate({
      to: '/pkg/$system/$name',
      params: { system: ecosystem, name: encodeURIComponent(trimmed) },
      search: { v: undefined },
    });
  }

  const isAdvisory = mode === 'advisory';

  return (
    <div className={styles.page}>
      <div className={homeStyles.hero}>
        <h1 className={styles.title}>PatchProbe</h1>
        <p className={styles.lead}>
          Look up package vulnerabilities, deprecations, and fix versions across npm, PyPI, Go,
          Maven, Cargo, NuGet, and RubyGems — powered by deps.dev, OSV.dev, and the NVD.
        </p>
      </div>

      <form onSubmit={handleSubmit} className={homeStyles.searchForm}>
        <div className={homeStyles.modeSwitch} role="tablist" aria-label="Search type">
          <button
            type="button"
            role="tab"
            aria-selected={!isAdvisory}
            className={`${homeStyles.modeTab} ${!isAdvisory ? homeStyles.modeTabActive : ''}`}
            onClick={() => switchMode('package')}
          >
            Package
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={isAdvisory}
            className={`${homeStyles.modeTab} ${isAdvisory ? homeStyles.modeTabActive : ''}`}
            onClick={() => switchMode('advisory')}
          >
            CVE / GHSA
          </button>
        </div>

        <div className={homeStyles.searchRow}>
          {!isAdvisory && (
            <select
              className={homeStyles.ecosystemSelect}
              value={ecosystem}
              onChange={(e) => setEcosystem(e.target.value as Ecosystem)}
              aria-label="Ecosystem"
            >
              {ECOSYSTEMS.map((eco) => (
                <option key={eco} value={eco}>
                  {ECOSYSTEM_LABELS[eco]}
                </option>
              ))}
            </select>
          )}

          <input
            className={homeStyles.packageInput}
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setAdvisoryError(false);
            }}
            placeholder={
              isAdvisory
                ? 'e.g. CVE-2021-44228 or GHSA-jfh8-c2jp-5v3q'
                : ECOSYSTEM_PLACEHOLDERS[ecosystem]
            }
            aria-label={isAdvisory ? 'Advisory id' : 'Package name'}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
          />

          <button type="submit" className={homeStyles.searchButton} disabled={!query.trim()}>
            {isAdvisory ? 'Look up' : 'Search'}
          </button>
        </div>

        {advisoryError && (
          <p className={homeStyles.searchError} role="alert">
            Expected a CVE-YYYY-NNNN or GHSA-xxxx-xxxx-xxxx id.
          </p>
        )}

        <p className={homeStyles.searchHint}>
          {isAdvisory
            ? 'Paste any CVE or GitHub Security Advisory id to see its details and affected packages.'
            : 'Pick an ecosystem and enter a package name to see its versions, advisories, and fix info.'}
        </p>
      </form>

      <footer className={homeStyles.footer}>
        Data sourced from{' '}
        <a href="https://deps.dev" target="_blank" rel="noreferrer">
          deps.dev
        </a>
        ,{' '}
        <a href="https://osv.dev" target="_blank" rel="noreferrer">
          OSV.dev
        </a>
        , and the{' '}
        <a href="https://nvd.nist.gov" target="_blank" rel="noreferrer">
          NVD
        </a>
        .
      </footer>
    </div>
  );
}
